import type { FunctionComponent } from 'react'

import {
	LanguageContext,
	useAllLanguages,
	useRegion,
} from '@monthio/react-components'
import { useContext, useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import { useLocation } from 'react-router-dom'

import type { RootState } from '../../store/reducers'
import type {
	AuthenticateSmartcheckSessionDto,
	SmartCheckConfigurationDto,
} from '../../types/authenticateSmartcheckSessionDto'
import type { HeaderStepData } from '../moduleUtils'

import { WhiteLabelSubdomainType } from '../../api/budgetApiClient'
import {
	getBudgetHelperHeaderSteps,
	getHeaderSteps,
	getStepIndexFromPathName,
} from '../moduleUtils'
import { SmartCheckHeader } from '../SmartCheckHeader/SmartCheckHeader'
import { LanguageSwitchButton } from './LanguageSwitch'

export interface HeaderProps {
	smartCheckSession?: AuthenticateSmartcheckSessionDto
	smartCheckConfiguration?: SmartCheckConfigurationDto
	consumerInputsStepIndex?: number
	shouldUseBudgetHelper: boolean
}

export const Header: FunctionComponent<HeaderProps> = ({
	smartCheckConfiguration,
	smartCheckSession,
	consumerInputsStepIndex,
	shouldUseBudgetHelper,
}) => {
	const { regionId } = useRegion()
	const { getRegionSupportedLanguageCodes, languages } = useAllLanguages()
	const { updateLanguage, language } = useContext(LanguageContext)
	const { pathname } = useLocation()
	const supportedLanguagesForButton = useMemo(() => {
		let supportedLanguages: string[] = getRegionSupportedLanguageCodes(
			regionId,
			'Flow',
		)

		if (shouldUseBudgetHelper) {
			supportedLanguages = supportedLanguages.filter(
				(x) => x === 'da-DK' || x === 'en-GB',
			)
		}

		return languages.filter(({ languageCode }) =>
			supportedLanguages.includes(languageCode),
		)
	}, [getRegionSupportedLanguageCodes, languages, regionId, shouldUseBudgetHelper])

	const hideProgressBar = smartCheckConfiguration?.shouldHideNavigationBar

	const [headerSteps, setHeaderSteps] = useState<HeaderStepData[]>([])
	useEffect(() => {
		if (shouldUseBudgetHelper) {
			setHeaderSteps(getBudgetHelperHeaderSteps())
		} else if (
			smartCheckSession &&
			smartCheckConfiguration &&
			!hideProgressBar
		) {
			getHeaderSteps(smartCheckSession).then((steps) => setHeaderSteps(steps))
		}
	}, [
		smartCheckSession,
		smartCheckConfiguration,
		hideProgressBar,
		shouldUseBudgetHelper,
	])

	return (
		<SmartCheckHeader
			steps={headerSteps}
			activeStep={getStepIndexFromPathName(
				pathname,
				headerSteps,
				consumerInputsStepIndex,
			)}
			clientLogoSrc={smartCheckConfiguration?.logo ?? ''}
			clientId={smartCheckSession?.client.id}
			languagesSelectComponent={
				<LanguageSwitchButton
					languages={supportedLanguagesForButton}
					value={language}
					onChange={updateLanguage}
				/>
			}
		/>
	)
}

export default connect((state: RootState) => {
	return {
		shouldUseBudgetHelper:
			state.flowEntryStatemachine.whitelabelSubdomainInfo?.type != null
				? state.flowEntryStatemachine.whitelabelSubdomainInfo?.type ===
					WhiteLabelSubdomainType.BudgetHelper
				: (state.authentication.smartCheckAuthenticationDto
						?.smartCheckConfiguration.shouldUseBudgetHelper ?? false),
	}
})(Header)
